import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { AlertTriangle, RefreshCw, CheckCircle, XCircle, CalendarIcon, Search, Upload, ExternalLink, Clock, Database } from "lucide-react";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

interface ReprocessResults {
  total_found: number;
  reprocessed: number;
  failed: number;
  errors: Array<{
    trace_id: string;
    error: string;
    details: any;
  }>;
  success_trace_ids: string[];
  failed_trace_ids: string[];
}

interface WebhookFailureStatus {
  trace_id: string;
  webhook_type: string;
  failure_stage: string;
  error_code?: string;
  error_message?: string;
  created_at: string;
  resolved_at?: string;
  retry_count: number;
}

export const WebhookFailureRecovery = () => {
  const [traceIds, setTraceIds] = useState('');
  const [webhookType, setWebhookType] = useState('obsync');
  const [failureStage, setFailureStage] = useState('');
  const [dateFrom, setDateFrom] = useState<Date | undefined>(undefined);
  const [dateTo, setDateTo] = useState<Date | undefined>(undefined);
  const [limit, setLimit] = useState('50');
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState<ReprocessResults | null>(null);
  const [webhookStatuses, setWebhookStatuses] = useState<WebhookFailureStatus[]>([]);
  const [statusLoading, setStatusLoading] = useState(false);
  const { toast } = useToast();

  const fetchWebhookStatuses = async () => {
    setStatusLoading(true);
    try {
      const { data, error } = await supabase
        .from('webhook_failures')
        .select('trace_id, webhook_type, failure_stage, error_code, error_message, created_at, resolved_at, retry_count')
        .order('created_at', { ascending: false })
        .limit(20);

      if (error) {
        console.error('Error fetching webhook statuses:', error);
        toast({
          title: "Error",
          description: "Failed to fetch webhook statuses",
          variant: "destructive",
        });
      } else {
        setWebhookStatuses(data || []);
      }
    } catch (error) {
      console.error('Error fetching webhook statuses:', error);
    } finally {
      setStatusLoading(false);
    }
  };

  useEffect(() => {
    fetchWebhookStatuses();
  }, []);

  const handleReprocess = async () => {
    setLoading(true);
    try {
      const traceIdArray = traceIds
        .split(/[,\s\n]+/)
        .map(id => id.trim())
        .filter(id => id.length > 0);

      const requestBody = {
        ...(traceIdArray.length > 0 && { trace_ids: traceIdArray }),
        ...(webhookType && { webhook_type: webhookType }),
        ...(failureStage && { failure_stage: failureStage }),
        ...(dateFrom && { 
          date_from: new Date(dateFrom.getFullYear(), dateFrom.getMonth(), dateFrom.getDate(), 0, 0, 0, 0).toISOString()
        }),
        ...(dateTo && { 
          date_to: new Date(dateTo.getFullYear(), dateTo.getMonth(), dateTo.getDate(), 23, 59, 59, 999).toISOString()
        }),
        limit: parseInt(limit) || 50
      };

      console.log('Reprocessing request:', requestBody);

      try {
        const { data, error } = await supabase.functions.invoke('reprocess-failed-webhooks', {
          body: requestBody
        });

        if (error) {
          console.error('Supabase function error:', error);
          throw new Error(error.message || 'Failed to invoke edge function');
        }

        if (!data) {
          throw new Error('No response data received from edge function');
        }

        setResults(data);
        
        // Refresh webhook statuses after reprocessing
        fetchWebhookStatuses();
        
        if (data.reprocessed > 0) {
          toast({
            title: "Success",
            description: `Successfully reprocessed ${data.reprocessed} webhook(s)`,
          });
        } else if (data.total_found === 0) {
          toast({
            title: "No Failed Webhooks Found",
            description: "No webhook failures match the specified criteria",
          });
        } else {
          toast({
            title: "Processing Complete",
            description: `Found ${data.total_found} failures, but could not reprocess any`,
            variant: "destructive",
          });
        }

      } catch (functionError) {
        console.error('Edge function call failed:', functionError);
        
        // Try alternative error handling
        let errorMessage = 'Unknown error occurred';
        if (functionError instanceof Error) {
          errorMessage = functionError.message;
        }
        
        toast({
          title: "Edge Function Error",
          description: `Failed to reprocess webhooks: ${errorMessage}. The function may not be deployed or accessible.`,
          variant: "destructive",
        });
      }

    } catch (error) {
      console.error('Error reprocessing webhooks:', error);
      toast({
        title: "Error",
        description: "Failed to reprocess webhooks",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleQuickFill = (traceId: string) => {
    setTraceIds(traceId);
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <RefreshCw className="h-5 w-5" />
            Webhook Failure Recovery
          </CardTitle>
          <CardDescription>
            Trace back and reprocess failed webhook events to restore data integrity
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Quick Fill Examples */}
          <div className="space-y-2">
            <Label className="text-sm font-medium">Quick Fill Examples</Label>
            <div className="flex flex-wrap gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuickFill('61fa9c74-726d-49a7-9107-f332512681b')}
              >
                <Upload className="h-3 w-3 mr-1" />
                61fa9c74-726d...
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => handleQuickFill('9b12a425-7f50-4285-9885-f53487a92ed7')}
              >
                <Upload className="h-3 w-3 mr-1" />
                9b12a425-7f50...
              </Button>
            </div>
          </div>

          <Separator />

          {/* Trace IDs Input */}
          <div className="space-y-2">
            <Label htmlFor="trace-ids">Trace IDs (comma or newline separated)</Label>
            <Textarea
              id="trace-ids"
              placeholder="Enter trace IDs from webhook failure alerts..."
              value={traceIds}
              onChange={(e) => setTraceIds(e.target.value)}
              rows={3}
            />
            <p className="text-xs text-muted-foreground">
              Copy trace IDs from Slack webhook failure alerts or leave empty to search by other criteria
            </p>
          </div>

          {/* Filter Options */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="webhook-type">Webhook Type</Label>
              <Input
                id="webhook-type"
                value={webhookType}
                onChange={(e) => setWebhookType(e.target.value)}
                placeholder="obsync"
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="failure-stage">Failure Stage</Label>
              <Input
                id="failure-stage"
                value={failureStage}
                onChange={(e) => setFailureStage(e.target.value)}
                placeholder="database_insert"
              />
            </div>
          </div>

          {/* Date Range */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label>Date From</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateFrom ? format(dateFrom, 'PPP') : 'Select start date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateFrom}
                    onSelect={setDateFrom}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>

            <div className="space-y-2">
              <Label>Date To</Label>
              <Popover>
                <PopoverTrigger asChild>
                  <Button variant="outline" className="w-full justify-start text-left font-normal">
                    <CalendarIcon className="mr-2 h-4 w-4" />
                    {dateTo ? format(dateTo, 'PPP') : 'Select end date'}
                  </Button>
                </PopoverTrigger>
                <PopoverContent className="w-auto p-0" align="start">
                  <Calendar
                    mode="single"
                    selected={dateTo}
                    onSelect={setDateTo}
                    initialFocus
                  />
                </PopoverContent>
              </Popover>
            </div>
          </div>

          {/* Limit */}
          <div className="space-y-2">
            <Label htmlFor="limit">Limit (max records to process)</Label>
            <Input
              id="limit"
              type="number"
              value={limit}
              onChange={(e) => setLimit(e.target.value)}
              placeholder="50"
              min="1"
              max="1000"
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-2">
            <Button
              onClick={handleReprocess}
              disabled={loading}
              className="flex-1"
            >
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  <Search className="h-4 w-4 mr-2" />
                  Find & Reprocess Failed Webhooks
                </>
              )}
            </Button>
            <Button
              variant="outline"
              onClick={() => window.open('https://supabase.com/dashboard/project/muopexzugnrnzyrtuqww/storage/buckets/webhook-failures', '_blank')}
            >
              <ExternalLink className="h-4 w-4 mr-2" />
              View Storage
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Results */}
      {results && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              {results.reprocessed > 0 ? (
                <CheckCircle className="h-5 w-5 text-green-500" />
              ) : (
                <AlertTriangle className="h-5 w-5 text-orange-500" />
              )}
              Reprocessing Results
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Summary */}
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center">
                <div className="text-2xl font-bold">{results.total_found}</div>
                <div className="text-sm text-muted-foreground">Found</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-green-600">{results.reprocessed}</div>
                <div className="text-sm text-muted-foreground">Reprocessed</div>
              </div>
              <div className="text-center">
                <div className="text-2xl font-bold text-red-600">{results.failed}</div>
                <div className="text-sm text-muted-foreground">Failed</div>
              </div>
            </div>

            {/* Success Trace IDs */}
            {results.success_trace_ids.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-green-600">Successfully Reprocessed</Label>
                <div className="flex flex-wrap gap-1">
                  {results.success_trace_ids.map((traceId) => (
                    <Badge key={traceId} variant="default" className="bg-green-100 text-green-800">
                      {traceId.substring(0, 8)}...
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Failed Trace IDs */}
            {results.failed_trace_ids.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium text-red-600">Failed to Reprocess</Label>
                <div className="flex flex-wrap gap-1">
                  {results.failed_trace_ids.map((traceId) => (
                    <Badge key={traceId} variant="destructive">
                      {traceId.substring(0, 8)}...
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {/* Error Details */}
            {results.errors.length > 0 && (
              <div className="space-y-2">
                <Label className="text-sm font-medium">Error Details</Label>
                <div className="space-y-2 max-h-60 overflow-y-auto">
                  {results.errors.map((error, index) => (
                    <div key={index} className="border rounded p-3 text-sm">
                      <div className="font-medium">{error.trace_id}</div>
                      <div className="text-red-600">{error.error}</div>
                      {error.details && (
                        <div className="text-muted-foreground text-xs mt-1">
                          {typeof error.details === 'string' ? error.details : JSON.stringify(error.details)}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
          )}
        </CardContent>
      </Card>
    )}

      {/* Webhook Status Monitor */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Database className="h-5 w-5" />
            Webhook Failure Status Monitor
          </CardTitle>
          <CardDescription>
            Recent webhook failures with reprocessing status and timestamps
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex justify-between items-center mb-4">
            <p className="text-sm text-muted-foreground">
              Showing last 20 webhook failures from the database
            </p>
            <Button
              variant="outline"
              size="sm"
              onClick={fetchWebhookStatuses}
              disabled={statusLoading}
            >
              {statusLoading ? (
                <RefreshCw className="h-4 w-4 animate-spin" />
              ) : (
                <RefreshCw className="h-4 w-4" />
              )}
              Refresh
            </Button>
          </div>

          {statusLoading ? (
            <div className="flex justify-center py-8">
              <RefreshCw className="h-6 w-6 animate-spin" />
            </div>
          ) : webhookStatuses.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No webhook failures found in the database
            </div>
          ) : (
            <div className="space-y-3 max-h-96 overflow-y-auto">
              {webhookStatuses.map((status) => (
                <div
                  key={status.trace_id}
                  className="border rounded-lg p-4 space-y-2"
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Badge
                        variant={status.resolved_at ? 'default' : 'destructive'}
                        className={status.resolved_at ? 'bg-green-100 text-green-800' : ''}
                      >
                        {status.resolved_at ? 'resolved' : 'failed'}
                      </Badge>
                      <code className="text-sm bg-muted px-2 py-1 rounded">
                        {status.trace_id}
                      </code>
                    </div>
                    <div className="flex items-center gap-2 text-sm text-muted-foreground">
                      <Clock className="h-4 w-4" />
                      {format(new Date(status.created_at), 'MMM dd, HH:mm')}
                    </div>
                  </div>

                  <div className="grid grid-cols-2 gap-4 text-sm">
                    <div>
                      <span className="font-medium">Type:</span> {status.webhook_type}
                    </div>
                    <div>
                      <span className="font-medium">Stage:</span> {status.failure_stage}
                    </div>
                    <div>
                      <span className="font-medium">Retry Count:</span> {status.retry_count}
                    </div>
                    {status.resolved_at && (
                      <div>
                        <span className="font-medium">Resolved At:</span>{' '}
                        {format(new Date(status.resolved_at), 'MMM dd, HH:mm')}
                      </div>
                    )}
                  </div>

                  {(status.error_code || status.error_message) && (
                    <div className="text-sm">
                      <span className="font-medium text-destructive">Error:</span>
                      <p className="text-muted-foreground mt-1">
                        {status.error_code && <span className="font-mono text-xs bg-destructive/10 px-1 py-0.5 rounded mr-2">{status.error_code}</span>}
                        {status.error_message && <span className="truncate">{status.error_message}</span>}
                      </p>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};